import { useState, useEffect } from 'react';
import { isColorDark, hexToRgba, resolveLinkProps } from '../utils';

export default function FloatingCtaSection({ block = {}, primaryColor, secondaryColor, isPreview }) {
    const { config = {}, content = {} } = block;
    const [visible, setVisible] = useState(!!isPreview);
    const [dismissed, setDismissed] = useState(false);

    const variant = config.variant || 'bar';
    const position = config.position || 'bottom';
    const showAfterPx = config.show_after_px || 600;
    const accent = primaryColor;
    const accentText = isColorDark(accent) ? '#ffffff' : '#111827';

    useEffect(() => {
        if (isPreview) return;
        // Check sessionStorage for dismissal
        const key = `floating_cta_${block.id || 'default'}_dismissed`;
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key)) {
            setDismissed(true);
            return;
        }

        const handler = () => {
            const scrollY = window.scrollY || window.pageYOffset;
            const docHeight = document.documentElement.scrollHeight;
            const winHeight = window.innerHeight;
            const nearBottom = scrollY + winHeight >= docHeight - 200;

            if (scrollY >= showAfterPx && !nearBottom) {
                setVisible(true);
            } else {
                setVisible(false);
            }
        };

        window.addEventListener('scroll', handler, { passive: true });
        handler(); // Check initial position
        return () => window.removeEventListener('scroll', handler);
    }, [showAfterPx]);

    const handleDismiss = () => {
        if (!isPreview) {
            setDismissed(true);
            const key = `floating_cta_${block.id || 'default'}_dismissed`;
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(key, '1');
            }
        }
    };

    if ((dismissed && !isPreview) || (!content.cta_text && !content.text)) return null;

    const show = isPreview ? true : visible;
    const lp = resolveLinkProps(content.cta_link || '#');

    // Close button
    const CloseBtn = ({ className = '', color = '#ffffff' }) => (
        <button onClick={handleDismiss} className={`shrink-0 p-1.5 rounded-full transition-colors hover:bg-white/20 ${className}`} style={{ color }} aria-label="Cerrar">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
    );

    // Social proof line
    const SocialProof = ({ color }) => {
        if (!content.show_social_proof || !content.social_proof_text) return null;
        return <p className="text-xs opacity-80" style={{ color }}>{content.social_proof_text}</p>;
    };

    // Bar variant — full-width bottom bar
    if (variant === 'bar') {
        return (
            <div
                className={`${isPreview ? 'relative w-full' : `fixed left-0 right-0 z-40 transition-transform duration-500 ease-out ${show ? 'translate-y-0' : 'translate-y-full'}`}`}
                style={{ bottom: 0 }}
            >
                <div className="backdrop-blur-lg border-t" style={{
                    backgroundColor: hexToRgba(accent, 0.92),
                    borderColor: hexToRgba('#ffffff', 0.15),
                }}>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-center gap-4 flex-wrap">
                        <div className="flex-1 min-w-0 text-center sm:text-left">
                            {content.text && (
                                <p className="text-sm font-medium truncate" style={{ color: accentText }}>{content.text}</p>
                            )}
                            <SocialProof color={accentText} />
                        </div>
                        {content.cta_text && (
                            <a {...lp}
                                className="shrink-0 px-6 py-2.5 rounded-full text-sm font-bold transition-all hover:scale-105 hover:shadow-lg shadow-md"
                                style={{
                                    backgroundColor: '#ffffff',
                                    color: accent,
                                }}>
                                {content.cta_text}
                            </a>
                        )}
                        {content.secondary_text && (
                            <span className="text-xs opacity-75 hidden sm:inline" style={{ color: accentText }}>{content.secondary_text}</span>
                        )}
                        <CloseBtn color={accentText} />
                    </div>
                </div>
            </div>
        );
    }

    // Pill variant — floating pill bottom-right
    if (variant === 'pill') {
        return (
            <div
                className={`${isPreview ? 'relative w-full flex justify-center' : `fixed z-40 transition-all duration-500 ease-out ${position === 'bottom-right' ? 'right-5 bottom-5' : 'left-1/2 -translate-x-1/2 bottom-5'} ${show ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'}`}`}
            >
                <div className="backdrop-blur-lg rounded-full shadow-2xl border flex items-center gap-3 pl-5 pr-2 py-2"
                    style={{
                        backgroundColor: hexToRgba(accent, 0.92),
                        borderColor: hexToRgba('#ffffff', 0.15),
                    }}>
                    <div className="min-w-0">
                        {content.text && (
                            <p className="text-sm font-medium whitespace-nowrap" style={{ color: accentText }}>{content.text}</p>
                        )}
                        <SocialProof color={accentText} />
                    </div>
                    {content.cta_text && (
                        <a {...lp}
                            className="shrink-0 px-5 py-2 rounded-full text-sm font-bold transition-all hover:scale-105 shadow-md"
                            style={{
                                backgroundColor: '#ffffff',
                                color: accent,
                            }}>
                            {content.cta_text}
                        </a>
                    )}
                    <CloseBtn color={accentText} />
                </div>
            </div>
        );
    }

    // Minimal variant — just a floating CTA button
    return (
        <div
            className={`${isPreview ? 'relative w-full flex justify-center' : `fixed z-40 right-5 bottom-5 transition-all duration-500 ease-out ${show ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-90 pointer-events-none'}`}`}
        >
            <div className="relative">
                <SocialProof color={accent} />
                <div className="flex items-center gap-2">
                    {content.cta_text && (
                        <a {...lp}
                            className="px-6 py-3 rounded-full text-sm font-bold transition-all hover:scale-105 shadow-2xl"
                            style={{
                                backgroundColor: accent,
                                color: accentText,
                            }}>
                            {content.cta_text}
                        </a>
                    )}
                    <button onClick={handleDismiss}
                        className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
                        style={{
                            backgroundColor: hexToRgba(accent, 0.15),
                            color: accent,
                        }}
                        aria-label="Cerrar">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
